import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import moment, { Moment } from 'moment';
import { holidays, workDays } from '../constants';

import { integerDeclination } from './text-formats';

/* Привести дату к формату ISO */
export const formatDateToISO = (date?: any): string => {
  date = date || moment();

  return moment(date).format('YYYY-MM-DDTHH:mm:ss');
};

/* Период отчёта: локальные сутки → UTC (как prepareDatesInData в журнале).
   Для сегодняшнего дня till — до текущего момента */
export const getReportPeriod = (
  dtFrom?: string | null,
  dtTill?: string | null
): { dt_from: string; dt_till: string } => {
  const till = moment(dtTill);
  const tillLocal = till.isSame(moment(), 'day')
    ? moment()
    : till.clone().endOf('day');

  return {
    dt_from: moment
      .utc(moment(dtFrom).startOf('day'))
      .format('YYYY-MM-DDTHH:mm:ss[Z]'),
    dt_till: moment.utc(tillLocal).format('YYYY-MM-DDTHH:mm:ss[Z]'),
  };
};

/* Привести дату к формату ISO с локальным временным смещением */
export const formatDateToISOutc = (date?: any): string => {
  date = date || moment.utc();

  return moment.utc(date).local().format('YYYY-MM-DDTHH:mm:ssZ');
};

//Преобразует дату UTC в локальную дату
export const toLocalMoment = (utcDate: string) => moment.utc(utcDate).local();

/* Привести дату к выбранному формату */
export const formatISODate = (date: any, format: string): string => {
  if (date && date instanceof Object) {
    date = date.isValid && date.isValid() ? date : moment();
  }

  date = date || moment();

  return moment(date, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').locale('ru').format(format);
};

export const getYearSuffix = (years: number): string => {
  years = Math.abs(years);
  const count = years % 100;

  if (count >= 5 && count <= 20) {
    return ' лет';
  }

  switch (years % 10) {
    case 1:
      return ' год';
    case 2:
    case 3:
    case 4:
      return ' года';
    default:
      return ' лет';
  }
};

export const getPassedDaysStr = (
  initialDate: Moment,
  overdueDate: Moment,
  full?: boolean
): string => {
  const now = cloneDeep(initialDate);
  const overdue = cloneDeep(overdueDate);

  if (now.isSame(overdue, 'day')) {
    return 'Сегодня';
  }

  const diffYears = now.diff(overdue, 'year');
  overdue.add(diffYears, 'years');
  const diffMonths = now.diff(overdue, 'months');
  overdue.add(diffMonths, 'months');
  const diffDays =
    now.diff(overdue, 'days') !== 0 ? now.diff(overdue, 'days') : 1;

  const yearsStr = diffYears
    ? Math.abs(diffYears) +
      (full
        ? getYearSuffix(diffYears)
        : `${getYearSuffix(diffYears).charAt(1)}.`)
    : '';
  const monthsStr = diffMonths
    ? Math.abs(diffMonths) + (full ? ' мес.' : 'м.')
    : '';
  const daysStr = diffDays ? Math.abs(diffDays) + (full ? ' д.' : 'д.') : '';

  const result = [yearsStr, monthsStr, daysStr]
    .filter((part) => part)
    .join(' ');

  if (now.isAfter(overdue)) {
    return `${full ? 'Через ' : ''}${result}`.trim();
  }

  return `${result}${full ? ' назад' : ''}`.trim();
};

/* Получение времени формата 00:01:19 */
export const getPassedTimeStr = (
  initialDate: Moment, //дата в будущем, до которой считаем
  overdueDate: Moment //сейчас
) => {
  const a = clone(initialDate);
  const b = clone(overdueDate);
  const hours = a.diff(b, 'hour');
  b.add(hours, 'hours');
  const minutes = a.diff(b, 'minutes');
  b.add(minutes, 'minutes');
  const seconds = a.diff(b, 'seconds');

  return `${
    hours ? (hours < 10 ? '0' + hours + ' :' : hours + ' :') : '00 :'
  } ${
    minutes ? (minutes < 10 ? '0' + minutes + ' :' : minutes + ' :') : '00 :'
  } ${seconds ? (seconds < 10 ? '0' + seconds : seconds) : '00'}`;
};

/* getPassedDaysStr + getPassedTimeStr (возвращает либо кол-во минут/часов, либо дней и т.д.) */
export const getPassedDaysOrTime = (
  initialDate: Moment, //сейчас
  overdueDate: Moment //дата в прошлом, от которой считаем
) => {
  const a = clone(initialDate);
  const b = clone(overdueDate);

  const years = a.diff(b, 'year');
  b.add(years, 'years');

  const months = a.diff(b, 'months');
  b.add(months, 'months');

  const days = a.diff(b, 'days');
  b.add(days, 'days');

  const hours = a.diff(b, 'hours');
  b.add(hours, 'hours');

  const minutes = a.diff(b, 'minutes');

  if (!years && !months && !days && !hours && !minutes) {
    return 'Сейчас';
  }

  return years || months || days
    ? `${years ? Math.abs(years) + 'г.' : ''} ${
        months ? Math.abs(months) + 'м.' : ''
      } ${days ? Math.abs(days) + 'д.' : ''} назад`
    : `${hours ? Math.abs(hours) + 'ч.' : ''} ${
        minutes ? Math.abs(minutes) + 'мин.' : ''
      } назад`;
};

/* Получение промежутка между датами в часах, минутах и секундах раздельно */
export const getTimeBetween = (start: Moment, end: Moment) => {
  //TODO: сейчас нигде не используется, но может пригодиться
  const a = clone(start);
  const b = clone(end);

  const hours = b.diff(a, 'hour');
  a.add(hours, 'hours');
  const minutes = b.diff(a, 'minutes');
  a.add(minutes, 'minutes');
  const seconds = b.diff(a, 'seconds');

  return {
    hours: hours,
    minutes: minutes,
    seconds: seconds,
  };
};

/* Получение количества лет, месяцев, дней и времени между двумя датами */
export const getElapsedTime = (
  old_date: any,
  new_date: any,
  short?: boolean,
  show_seconds?: boolean,
  short_without_minutes?: boolean
): string => {
  const shortFormat = short || false;
  const showSeconds = show_seconds !== undefined ? show_seconds : true;
  const shortWithoutMinutes =
    short_without_minutes !== undefined ? short_without_minutes : true;

  const oldDate: any = moment(old_date);
  let newDate: any = moment(new_date);

  let fullStr = '';

  let days = 0;
  newDate = moment(newDate - oldDate.seconds() * 1000);
  const seconds = newDate.format('ss');
  newDate = moment(newDate - oldDate.minutes() * 60 * 1000);
  const minutes = newDate.format('mm');
  newDate = moment(newDate - oldDate.hours() * 60 * 60 * 1000);
  const hours = newDate.format('HH');

  if (oldDate.date() === newDate.date()) {
    days = 0;
  } else {
    newDate =
      oldDate.date() > newDate.date()
        ? moment(newDate - (oldDate.date() - 1) * 24 * 60 * 60 * 1000)
        : moment(newDate - oldDate.date() * 24 * 60 * 60 * 1000);
    days = newDate.date();
  }

  const ny = newDate.year();
  const oy = oldDate.year();
  const nm = newDate.month() + 1;
  const om = oldDate.month() + 1;
  const years = Math.floor(((ny - oy) * 12 + nm - om) / 12);
  const months = ((ny - oy) * 12 + nm - om) % 12;

  const strYear =
    years % 10 == 1 && years % 100 != 11
      ? 'г.' //' год'
      : years % 10 > 1 && years % 10 < 5 && !(years >= 10 && years <= 19)
        ? 'г.' //' года'
        : 'г.'; //' лет';
  const strMonth =
    months % 10 == 1 && months % 100 != 11
      ? 'м.' //' месяц'
      : months % 10 > 1 && months % 10 < 5 && !(months >= 10 && months <= 19)
        ? 'м.' //' месяца'
        : 'м.'; //' месяцев';
  const strDate =
    days % 10 == 1 && days % 100 != 11
      ? 'д.' //' день'
      : days % 10 > 1 && days % 10 < 5 && !(days >= 10 && days <= 19)
        ? 'д.' // ' дня'
        : 'д.'; // ' дней';

  const yearsString = years ? years + strYear : '';
  const monthString = !months && !years ? '' : ' ' + months + strMonth;
  const daysString = !months && !years && !days ? '' : ' ' + days + strDate;

  if (shortFormat) {
    if (years || months || days) {
      fullStr = yearsString + monthString + daysString;
    } else {
      if (shortWithoutMinutes) {
        fullStr = hours + ':' + minutes + ':' + seconds;
      }
    }
  } else {
    if (showSeconds) {
      fullStr =
        yearsString +
        monthString +
        daysString +
        ' ' +
        hours +
        ':' +
        minutes +
        ':' +
        seconds;
    } else {
      fullStr =
        yearsString + monthString + daysString + ' ' + hours + ':' + minutes;
    }
  }

  return fullStr.trim();
};

/* Преобразования календарных дат в параметрах запроса */
const dateFields = [
  'created_at__gte',
  'created_at__lte',
  'execution__desired_start_at__gte',
  'execution__desired_end_at__lte',
  'execution__end_at__lte',
  'execution__end_at__gte',
  'actions__lift__start_at__gte',
  'actions__lift__end_at__lte',
  'actions__central_heating__start_at__gte',
  'actions__central_heating__end_at__lte',
  'actions__hot_water_supply__start_at__gte',
  'actions__hot_water_supply__end_at__lte',
  'actions__cold_water_supply__start_at__gte',
  'actions__cold_water_supply__end_at__lte',
  'actions__electricity__start_at__gte',
  'actions__electricity__end_at__lte',
];

export const prepareDatesInData = (data: { [key: string]: any }) => {
  const result = clone(data);
  dateFields.forEach((field: string) => {
    if (data.hasOwnProperty(field)) {
      result[field] = moment
        .utc(result[field])
        .format('YYYY-MM-DDTHH:mm:ss[Z]');
    }
  });

  return result;
};

//TODO: ИСПОЛЬЗУЕТСЯ ТОЛЬКО В ЗАЯВКАХ!!!
export const normalizeActionDates = (params: Record<string, any>) => {
  const startDates: string[] = [];
  const endDates: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (!key.startsWith('actions__')) {
      return;
    }

    if (key.endsWith('__start_at__gte') && typeof value === 'string') {
      startDates.push(value);
      delete params[key];
    }

    if (key.endsWith('__end_at__lte') && typeof value === 'string') {
      endDates.push(value);
      delete params[key];
    }

    if (startDates.length) {
      params['actions__start_at__gte'] = startDates.reduce((min, d) =>
        d < min ? d : min
      );
    }

    if (endDates.length) {
      params['actions__end_at__lte'] = endDates.reduce((max, d) =>
        d > max ? d : max
      );
    }
  });

  return params;
};

/*Массив из любого числа дат (универсальная версия getThreeDaysArray)*/
export const getSomeDaysArray = (firstDate: any, amount: number) => {
  const first = moment(firstDate).startOf('day');
  const daysArray = [formatDateToISO(first)];

  for (let i = 1; i <= amount - 1; i++) {
    daysArray.push(formatDateToISO(first.add(1, 'd')));
  }

  //moment(day).format('dd') сделает "пт", "сб", "вс" и т.п.
  return daysArray;
};

/*Форматирование числа в hh:mm:ss, timer принимает кол-во секунд, флаг displayHours отвечает за отображение часов*/
export const formatTimer = (timer: number, displayHours: boolean): string => {
  const pad = (num: number) => (num < 10 ? `0${num}` : num);

  const hours = Math.floor(timer / 3600);
  const minutes = Math.floor(timer / 60) - hours * 60;
  const seconds = Math.floor(timer - hours * 3600 - minutes * 60);

  return displayHours
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
};

/*Возвращает отметки по часам на временном промежутке. ВЫЗЫВАТЬ ЧЕРЕЗ memo!*/
export const getTimeRange = (
  startTime: any,
  endTime: any,
  unit: 'hours' | 'minutes'
  // part: number = 1 //виснет бразуер при указании числа отличного от 1
): string[] => {
  //https://stackoverflow.com/questions/37673322/how-to-get-date-range-using-moment-js (почти то же самое)
  //TODO: сделать универсальной ?! (может уже и сделано)
  const start = moment(startTime);
  const end = moment(endTime);
  const range: any[] = [];

  const diff = end.diff(start, unit);

  for (let i = 0; i <= diff; i++) {
    //вместо i++ было i+part, но почему-то зависал браузер
    range.push(formatDateToISOutc(moment(start).add(i, unit)));
  }

  return range;
};

export const getWeekendDates = (startDate: Moment, endDate: Moment) => {
  const weekendDates = [];

  while (startDate.isSameOrBefore(moment(endDate), 'day')) {
    if (startDate.day() === 0 || startDate.day() === 6) {
      weekendDates.push(startDate.toDate());
    }

    startDate = startDate.add(1, 'day');
  }

  return weekendDates;
};

export const isHolidayDay = (day: Moment): boolean => {
  return (
    (['сб', 'вс'].includes(moment(day).format('dd')) &&
      !workDays.includes(day.format('DD.MM.yyyy'))) ||
    holidays.includes(day.format('DD.MM.yyyy'))
  );
};

/* Возвращает строку с разницей между двумя датами в формате Часы:Минуты:Секунды
 * Если, как минимум, одной из дат оказалась пустая строка, вернет 00:00:00
 * В отличие от getTimeBetween хронологический порядок дат не имеет значения */
export const getTimeDurationBetweenDates = (
  firstISODate: string,
  secondISODate: string
): string => {
  if (!firstISODate || !secondISODate) {
    return '00:00:00';
  }

  const start: Moment = moment(firstISODate);
  const end: Moment = moment(secondISODate);

  if (!start.isValid() || !end.isValid()) {
    return '00:00:00';
  }

  const requestTime: string[] = [];

  const durationInSeconds: number = Math.ceil(
    Math.abs(moment.duration(end.diff(start)).asMilliseconds()) / 1000
  );

  const durationTime: { [key: string]: number } = {
    hours: Math.floor(durationInSeconds / 3600),
    minutes: Math.floor((durationInSeconds % 3600) / 60),
    seconds: Math.ceil(durationInSeconds % 60),
  };

  for (const value in durationTime) {
    if (durationTime[value] <= 9) {
      requestTime.push(`0${durationTime[value]}`);
    } else {
      requestTime.push(`${durationTime[value]}`);
    }
  }

  return requestTime.join(':');
};

/**
 * Форматирует дату в зависимости от того, является ли она сегодняшней, вчерашней или более ранней.
 *
 * @param {string} initialDate - Исходная дата в строковом формате.
 * Дата должна быть в формате, поддерживаемом библиотекой Moment.js.
 *
 * @returns {string} - Форматированная строка даты.
 * Возвращает "Сегодня, HH:mm", если дата совпадает с сегодняшней,
 * "Вчера, HH:mm", если дата совпадает с вчерашней,
 * или "DD.MM.yyyy, HH:mm" для всех остальных дат.
 */

export const getFormattedDate = (initialDate: string): string => {
  const date = moment(initialDate);
  const dateStartOfDay = date.clone().startOf('day');
  const todayStartOfDay = moment().startOf('day');

  if (dateStartOfDay.isSame(todayStartOfDay)) {
    return `Сегодня, ${date.format('HH:mm')}`;
  }

  if (dateStartOfDay.isSame(todayStartOfDay.subtract(1, 'day'))) {
    return `Вчера, ${date.format('HH:mm')}`;
  }

  return date.format('DD.MM.yyyy, HH:mm');
};

/**
 * Преобразует строку времени в формате "HH:mm" в количество минут.
 *
 * @param {string} time - Время в формате "HH:mm".
 * @returns {number} Количество минут, соответствующее указанному времени.
 *
 * @example
 * // Возвращает 150
 * convertToMinutes("02:30");
 *
 * @example
 * // Возвращает 75
 * convertToMinutes("01:15");
 */
export const convertToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return NaN;
  }

  return hours * 60 + minutes;
};

/* Возвращает дату из прошлого в относительном формате */
export const getRelativeDate = (date: string | Moment): string => {
  const enteredDate = moment(date);
  const now = moment();
  const diffDays = now.diff(enteredDate, 'days');

  if (diffDays === 0) {
    return 'Сегодня';
  }

  if (diffDays === 1) {
    return 'Вчера';
  }

  if (diffDays === 2) {
    return 'Позавчера';
  }

  if (diffDays <= 6) {
    return `${diffDays} ${integerDeclination(diffDays, [
      'день',
      'дня',
      'дней',
    ])} назад`;
  }

  if (diffDays <= 28) {
    const week = Math.floor(diffDays / 7);

    return week === 1 ? 'неделю назад' : `${week} недели назад`;
  }

  if (diffDays <= 365) {
    const months = Math.floor(diffDays / 30);

    return `${months} ${integerDeclination(months, [
      'месяц',
      'месяца',
      'месяцев',
    ])} назад`;
  }

  const years = Math.floor(diffDays / 365);

  return `${years > 1 ? years + ' ' : ''}${integerDeclination(years, [
    'год',
    'года',
    'лет',
  ])} назад`;
};

/** ВНИМАНИЕ! Прибавляет +3 часа ко времени с бэка, из-за неразберихи с форматами **/
/* Преобразует дату в зависимости от даты:
  1. Если это текущий день - тогда выводим время - "12:55" или "Сегодня в 12:55" (full)
  2. Если эта текущая неделя - выводит день недели - "Вт" или "Вт в 12:55" (full)
  3. Если дата позже - выводим дату формата "22.06.2025" или "22.06.2025 в 12:55" (full)
*/
export const formatMessageDate = (value: string, full?: boolean) => {
  const now = moment();
  const date = moment.utc(value).local();

  if (now.isSame(date, 'day')) {
    return full ? `Сегодня в ${date.format('HH:mm')}` : date.format('HH:mm');
  } else if (now.isSame(date, 'week')) {
    return full
      ? `${date.format('dd')} в ${date.format('HH:mm')}`
      : date.format('dd');
  }

  return full
    ? `${date.format('DD.MM.yyyy')} в ${date.format('HH:mm')}`
    : date.format('DD.MM.yyyy');
};

/* Форматирует ISO-дату в формат "ДД.ММ.ГГГГ/ЧЧ:мм". Если дата не передана, возвращает пустую строку */
export const formatDateWithSlash = (date: string | null) =>
  date ? moment(date).format('DD.MM.YYYY/HH:mm') : '';

/* Форматирует две ISO-даты в диапазон. Для "Фактическое время" — формат "ДД.ММ.ГГГГ/ЧЧ:мм-ЧЧ:мм" или "ДД.ММ.ГГГГ/ЧЧ:мм-ДД.ММ.ГГГГ/ЧЧ:мм"
 * @param format - 'concat' (для склеивания) | 'base' (стандартное форматирование)
 */
export const formatTwoDates = (first: string | null, second: string | null) => {
  if (!first || !second) {
    return '';
  }

  const isSameDay = moment(first).isSame(moment(second), 'day');

  if (isSameDay) {
    const date = moment(first).format('DD.MM.YYYY');
    const time1 = moment(first).format('HH:mm');
    const time2 = moment(second).format('HH:mm');

    return `${date}/${time1}-${time2}`;
  }

  return `${formatDateWithSlash(first)}-${formatDateWithSlash(second)}`;
};
